import { Injectable } from '@angular/core';
import { NGXLogger as LoggerService } from "ngx-logger";
import { ChoiceClass, ChoiceEnum, MutltipleChoice, OptionClass, SecondaryAnswer, TimePeriod, VoteClass } from 'whocan-lib';
import { OptionsService } from '../shared/options/options.service';
import { VotesService } from '../features/vote/votes.service';
import { HeaderService } from '../shared/poll-header/header.service';
import { AuthService } from '../../user/auth.service';
import { TemplateService } from './poll-templates/template.service';
import { UserVoteService } from '../features/vote/user-vote.service';
import { VoteCountsService } from './vote-counts.service';


export interface BestOptionsData {
    option: OptionClass, yes: number, no: number, maybe: number
}

export interface AnswersPerOptionPerUser {
    userName: string,
    userId: string,
    sumPerUser: number,
    answerArray?: { textAnswer?: string, count?: number }[]
}

@Injectable({
    providedIn: 'root'
})
/**
 *For getting derrivated attributes like yesVoters, participants, maxIsReached
* The data is not hold directly here but in the unlerlying OptionsService, VotesService etc
 * One source: every count, name list and limit reads votesService.voteClassList - the votes as the
 * database holds them. The current voter's form (UserVoteService.userVote) is not in that list, so an
 * unsaved answer shows up nowhere here (concept-own-form-state-2026-09-18).
 */
export class PollResultsService {
    constructor(
        private logger: LoggerService,
        private votesService: VotesService,
        private headerService: HeaderService,
        public optionsService: OptionsService,
        private authService: AuthService,
        private templateService: TemplateService,
        private userVoteService: UserVoteService,
        private voteCountsService: VoteCountsService,
    ) {
    }

    /**
     * True while the limit aggregates come from the public voteCounts node instead of
     * the vote list (participant on a poll with showParticipants=false - the list then
     * only holds the user's own vote, see workorder-option-counts-2026-08-31).
     */
    get isCountsBacked(): boolean {
        return this.voteCountsService.isActive;
    }

    /**
     * YES + MAYBE responses for the option - count-backed in the blind case,
     * aggregated from the vote list everywhere else (that path stays untouched).
     */
    public getPositiveResponsesCount(optionIndex: number): number {
        if (this.isCountsBacked) return this.voteCountsService.getPositiveCount(optionIndex);
        return this.getQuantityOfYesVoters(optionIndex) + this.getQuantityOfMayeVoters(optionIndex);
    }

    /**
     * Registered persons (sum of choice.adultsCount where YES) for a willJoin option -
     * count-backed in the blind case, aggregated from the vote list everywhere else.
     */
    public getRegisteredPeopleSum(optionIndex: number): number {
        if (this.isCountsBacked) return this.voteCountsService.getPersonsSum(optionIndex);
        return this.getRegisteredPeopleAnswersSum(optionIndex);
    }

    // ---------------------------------------------------------------------------------
    // Header people limit (workorder-header-max-persons-2026-09-10, task 4)
    //
    // Deliberate inconsistency with the option limit (decision 8): the option limit counts
    // choice.adultsCount only (a kid needs no course slot), the header limit counts persons
    // incl. kids (a kid needs a seat). Do not align the two.
    //
    // Display and gate are separate (workorder-max-persons-chip-true-remaining-2026-09-17):
    // the chip shows the event's remaining capacity (personsLeftTotal, same number as the
    // dashboard tile), the yes-gate and the number field use the voter's quota
    // (personsLeftForUser), which leaves the voter's own saved acceptance out.
    // ---------------------------------------------------------------------------------

    /** the event-wide people limit; undefined when not set or when the register block is off */
    get maxPersons(): number | undefined {
        const header = this.headerService.headerClass;
        if (!header?.registerPeople) return undefined;
        return header.maxPersons;
    }

    /**
     * Registered persons of everybody EXCEPT the current voter (persisted state only).
     * Counts-backed: the aggregate holds all persisted votes incl. the voter's own saved
     * one, which is subtracted again. List-backed: the voter's own list entry is the
     * persisted vote; it is skipped here and added back from the baseline in
     * registeredPersonsTotal, so the limit maths never reads the form.
     */
    get othersRegisteredPersons(): number {
        if (this.isCountsBacked) {
            return Math.max(0, this.voteCountsService.getPersonsTotal() - this.userVoteService.persistedRegisteredPersons);
        }
        const ownUserId = this.userVoteService.userVote?.userId;
        let total = 0;
        this.votesService?.voteClassList.forEach(vote => {
            if (ownUserId && vote.userId === ownUserId) return;
            total += vote.registeredPersons;
        });
        return total;
    }

    /** all registered persons incl. the current voter's PERSISTED contribution */
    get registeredPersonsTotal(): number {
        if (this.isCountsBacked) return this.voteCountsService.getPersonsTotal();
        return this.othersRegisteredPersons + this.userVoteService.persistedRegisteredPersons;
    }

    /**
     * Quota for the current voter - NOT the event's remaining capacity. Display uses
     * personsLeftTotal. Limit minus everybody else: a voter who is already in keeps their
     * own saved quota as free spots (decision 4: editing stays possible). Never negative;
     * undefined without a limit.
     */
    get personsLeftForUser(): number | undefined {
        const maxPersons = this.maxPersons;
        if (!maxPersons) return undefined;
        return Math.max(0, maxPersons - this.othersRegisteredPersons);
    }

    /**
     * The event's remaining capacity, identical for every viewer: limit minus everybody's
     * PERSISTED registration incl. the current voter's own. This is what the chip and the
     * dashboard tile show - do not use it for the yes-gate (see personsLeftForUser).
     */
    get personsLeftTotal(): number | undefined {
        const maxPersons = this.maxPersons;
        if (!maxPersons) return undefined;
        return Math.max(0, maxPersons - this.registeredPersonsTotal);
    }

    /**
     * "Full" for the current voter: no spot left AND no saved acceptance of their own.
     * Whoever is already in keeps seeing (and may keep) their own quota.
     */
    get maxPersonsReached(): boolean {
        const left = this.personsLeftForUser;
        if (left === undefined) return false;
        return left <= 0 && this.userVoteService.persistedRegisteredPersons === 0;
    }

    /**
     * Persons the current voter may register: what is left, but never less than the quota
     * already saved - when others overbooked the event (race, decision 3) an existing
     * acceptance may still be re-saved unchanged, only new or increased acceptances are
     * blocked (decision 4).
     */
    get personsAllowedForUser(): number | undefined {
        const left = this.personsLeftForUser;
        if (left === undefined) return undefined;
        return Math.max(left, this.userVoteService.persistedRegisteredPersons);
    }

    /**
     * DISPLAY only, not the save rule (workorder-matrix-header-limit-exceeded-warning-2026-09-26, E2):
     * the row with the given form would put the event over its limit - everybody else's persisted
     * persons plus what the form registers. Unlike personsAllowedForUser / getIsMaxPersonsExceeded
     * this also flags an overbooking that is already saved (old votes from before the limit, a race),
     * which the save keeps accepting unchanged (decision 4). Only meaningful for the current voter's
     * own form row: othersRegisteredPersons leaves the OWN persisted vote out.
     */
    public isHeaderLimitExceededWithForm(vote: VoteClass): boolean {
        const maxPersons = this.maxPersons;
        if (!maxPersons || vote?.choiceInvitation !== 1) return false;
        return this.othersRegisteredPersons + vote.registeredPersons > maxPersons;
    }

    public getYesVoters(optionIndex: number): string[] {
        const returnValue: string[] = [];
        this.votesService?.voteClassList.forEach((vote) => {
            const choiceList = vote.choiceList
            if (choiceList) {
                if (choiceList[optionIndex]?.isYes) {
                    returnValue.push(this.getUserName(vote));
                }
            }
        });
        return returnValue;
    }

    public getMaybeVoters(optionIndex: number): string[] {
        const returnValue: string[] = [];
        this.votesService?.voteClassList.forEach((vote) => {
            const choiceList = vote.choiceList
            if (choiceList) {
                if (choiceList[optionIndex]?.isMaybe) {
                    returnValue.push(this.getUserName(vote));
                }
            }
        });
        return returnValue;
    }

    public getNoVoters(optionIndex: number): string[] {
        const returnValue: string[] = [];
        this.votesService?.voteClassList.forEach((vote) => {
            const choiceList = vote.choiceList
            if (choiceList) {
                if (!choiceList[optionIndex] ||
                    choiceList[optionIndex]?.isNo ||
                    choiceList[optionIndex].choiceEnum === ChoiceEnum.notSelected
                ) {
                    returnValue.push(this.getUserName(vote));
                }
            }
        });
        return returnValue;
    }

    /**
     * Yes and Maybe voters
     * @param optionIndex
     * @returns
     */
    public getPositiveVoters(optionIndex: number): string[] {
        const returnValue: string[] = [];
        this.votesService?.voteClassList.forEach((vote, voteIndex) => {
            const choiceList = vote.choiceList
            if (choiceList) {
                if (choiceList[optionIndex]?.isYes ||
                    choiceList[optionIndex]?.isMaybe
                ) {
                    returnValue.push(this.getUserName(vote));
                }
            }
        });
        return returnValue;
    }


    public getRegisteredPeopleAnswers(optionIndex: number): { userName: string, count: number }[] {
        const returnValue: { userName: string, count: number }[] = [];
        this.votesService?.voteClassList.forEach((vote, voteIndex) => {
            if (vote.choiceList) {
                const answer: { userName: string, count: number } = {
                    userName: this.getUserName(vote),
                    count: 0,
                }
                const choice = vote.choiceList[optionIndex]
                if (choice?.isYes && choice?.adultsCount) {
                    answer.count = choice?.adultsCount
                    returnValue.push(answer)
                }
            }
        });
        return returnValue;
    }

    public getNumberOfParticipants(option: OptionClass): number {
        if (option.optionConfig.pimaryAnswer === 'willJoin') {
            return this.getRegisteredPeopleSum(option.index)
        } else {
            return this.getPositiveResponsesCount(option.index)
        }
    }

    /**
     * Everybody but the current voter. One formula for both sources: the list total contains the
     * own persisted entry, the voteCounts aggregate contains the own saved vote - and the own
     * list entry is exactly that saved vote in both cases (counts-backed: the only entry).
     */
    public getNumberOfParticipantsExcludingUser(option: OptionClass): number {
        return this.getNumberOfParticipants(option) - this.ownPersistedContribution(option);
    }

    /** the current voter's own PERSISTED contribution to the option's total - what the list entry says, 0 without one */
    public ownPersistedContribution(option: OptionClass): number {
        return this.getChoiceContribution(this.userVoteService.ownPersistedVote?.choiceList[option.index], option);
    }

    /** what one choice adds to the option's total (getNumberOfParticipants): the registered adults for willJoin, one for yes/maybe otherwise */
    public getChoiceContribution(choice: ChoiceClass | undefined, option: OptionClass): number {
        if (!choice) return 0;
        if (option.optionConfig.pimaryAnswer === 'willJoin') return choice.isYes && choice.adultsCount ? choice.adultsCount : 0;
        return (choice.isYes || choice.isMaybe) ? 1 : 0;
    }

    public getRegisteredPeopleAnswersSum(optionIndex: number): number {
        let returnValue = 0;
        this.getRegisteredPeopleAnswers(optionIndex).forEach(answer => {
            returnValue += answer.count
        })
        return returnValue
    }

    public getUserName(vote: VoteClass): string {
        if (vote.userName) {
            return vote.userName
        } else if (vote.userId === this.authService.uid) {
            return this.templateService.getLabel('vote', 'you')
        }
        return this.templateService.getLabel('vote', 'noName')
    }

    public getTextAndNumberAnswers(optionIndex: number): AnswersPerOptionPerUser[] {
        const returnValue: AnswersPerOptionPerUser[] = [];
        this.votesService?.voteClassList.forEach((vote, voteIndex) => {
            if (vote.choiceList) {
                let addToAnswers: boolean = false
                const textAndNumberAnswer: AnswersPerOptionPerUser = {
                    userName: this.getUserName(vote),
                    userId: vote.userId,
                    sumPerUser: 0,
                    answerArray: []
                }
                vote.choiceList[optionIndex]?.secondaryAnswerList?.forEach((answer) => {
                    if (
                        answer.text
                    ) {
                        const count = answer.count ? answer.count : 0
                        textAndNumberAnswer.answerArray.push({
                            textAnswer: answer.text,
                            count: count
                        });
                        textAndNumberAnswer.sumPerUser += count;
                        addToAnswers = true
                    }
                })
                if (addToAnswers) {
                    returnValue.push(textAndNumberAnswer)
                }
            }
        });
        return returnValue;
    }

    public getNumberAnswers(optionIndex: number): AnswersPerOptionPerUser[] {
        const returnValue: AnswersPerOptionPerUser[] = [];
        this.votesService?.voteClassList.forEach((vote, voteIndex) => {
            if (vote.choiceList) {
                let addToAnswers: boolean = false
                const textAndNumberAnswer: AnswersPerOptionPerUser = {
                    userName: this.getUserName(vote),
                    userId: vote.userId,
                    sumPerUser: 0,
                    answerArray: []
                }
                vote.choiceList[optionIndex]?.secondaryAnswerList?.forEach((answer) => {
                    if (
                        answer.count !== undefined && answer.count !== null
                    ) {
                        textAndNumberAnswer.answerArray.push({
                            textAnswer: answer.text,
                            count: answer.count
                        });
                        textAndNumberAnswer.sumPerUser += answer.count;
                        addToAnswers = true
                    }
                })
                if (addToAnswers) {
                    returnValue.push(textAndNumberAnswer)
                }
            }
        });
        return returnValue;
    }

    getSumOfNumberAnswers(optionIndex: number): number {
        let returnValue: number = 0;
        this.votesService?.voteClassList.forEach((vote, voteIndex) => {
            const choiceList = vote.choiceList
            if (choiceList) {
                choiceList[optionIndex]?.secondaryAnswerList?.forEach((answer) => {
                    if (answer.count) {
                        returnValue += answer.count
                    }
                })
            }
        });
        return returnValue;
    }

    getAverageOfNumberAnswers(optionIndex: number): number {
        let sum: number = 0;
        let numberOfVoters: number = 0
        this.votesService?.voteClassList.forEach((vote, voteIndex) => {
            const choiceList = vote.choiceList
            if (choiceList) {
                choiceList[optionIndex]?.secondaryAnswerList.forEach((answer) => {
                    if (answer.count) {
                        numberOfVoters += 1
                        sum += answer?.count
                    }
                })
            }
        });
        if (numberOfVoters > 0) {
            return parseFloat((sum / numberOfVoters).toFixed(2));
        } else {
            return 0
        }
    }

    private getQuantityOfNoVoters(optionIndex: number): number {
        return this.getNoVoters(optionIndex).length
    }

    public getQuantityOfYesVoters(optionIndex: number): number {
        return this.getYesVoters(optionIndex).length
    }
    public getQuantityOfMayeVoters(optionIndex: number): number {
        return this.getMaybeVoters(optionIndex).length
    }


    /** names of everybody who answered */
    public getParticipants(): string[] {
        const participants: string[] = [];
        this.votesService.voteClassList.forEach(element => {
            participants.push(this.getUserName(element));
        });
        return participants;
    }

    public getMaxNumberOfEntries(optionIndex: number): number {
        let maxNumberOfEntries: number = this.optionsService.optionList.getByOptionIndex(optionIndex)?.maxNumberOfEntries;
        if (!maxNumberOfEntries) {// check if we have a max on the poll level
            maxNumberOfEntries = this.headerService.headerClass.limitPerOption;
        }
        return maxNumberOfEntries;
    }

    public isMaxNumberSet(optionIndex): boolean {
        if (this.getMaxNumberOfEntries(optionIndex) > 0) {
            return true;
        }
        return false;
    }

    public secondaryAnswerMaxReached(option: OptionClass): boolean {
        const sum = this.getSumOfNumberAnswers(option.index)
        const maxNumberOfEntries = option.secondaryAnswerMax
        if (!maxNumberOfEntries) return false
        return sum >= maxNumberOfEntries
    }

    // ---------------------------------------------------------------------------------
    // Option people limit (option.maxNumberOfEntries / header.limitPerOption)
    //
    // Display and gate are separate, like the header limit above
    // (workorder-option-chip-true-remaining-2026-09-18): the chip shows the option's remaining
    // capacity (optionSpotsLeftTotal, same number as the persons-summary of the card), the
    // yes-gate uses maxParticipantsReached, which leaves the voter's own saved contribution out.
    // The chip pairs optionSpotsLeftTotal (display, every viewer) with optionAllowedForUser (the
    // voter's own upper bound, workorder-chip-own-limit-2026-09-19); maxParticipantsReached is the yes-gate.
    // ---------------------------------------------------------------------------------

    /** the option is full for the current voter: the others alone reach the limit (an own saved acceptance never blocks its own re-save) */
    public maxParticipantsReached(option: OptionClass): boolean {
        const max = this.getMaxNumberOfEntries(option.index);
        if (!max) return false;
        return this.getNumberOfParticipantsExcludingUser(option) >= max;
    }

    /**
     * The option's remaining capacity, identical for every viewer: limit minus everybody's persisted
     * contribution incl. the current voter's own. This is what the option chip shows - do not use it
     * for the yes-gate, that is maxParticipantsReached (voter-specific). Same display/gate split as the
     * header limit (personsLeftTotal vs personsLeftForUser, workorder-max-persons-chip-true-remaining-2026-09-17).
     * undefined without a limit on the option.
     */
    public optionSpotsLeftTotal(option: OptionClass): number | undefined {
        const max = this.getMaxNumberOfEntries(option.index);
        if (!max) return undefined;
        return Math.max(0, max - this.getNumberOfParticipants(option));
    }

    /**
     * How far the current voter may go on this option: everybody else subtracted from the limit, but never
     * less than the voter's own saved contribution - an existing registration may always be re-saved unchanged,
     * only increases are blocked (same rule as personsAllowedForUser for the header, decision 4).
     * The chip shows it next to optionSpotsLeftTotal, getOptionMaxExceeded enforces it. undefined without a limit.
     */
    public optionAllowedForUser(option: OptionClass): number | undefined {
        const max = this.getMaxNumberOfEntries(option.index);
        if (!max) return undefined;
        return Math.max(max - this.getNumberOfParticipantsExcludingUser(option), this.ownPersistedContribution(option));
    }

    /**
     * DISPLAY only, not the save rule (workorder-matrix-header-limit-exceeded-warning-2026-09-26, E3):
     * the option would be over its limit with the given form - everybody else's persisted contribution
     * plus the form's choice. Unlike getOptionMaxExceeded this also flags an overbooking that is already
     * saved, which the save keeps accepting unchanged. Only meaningful for the current voter's own form
     * row: getNumberOfParticipantsExcludingUser leaves the OWN persisted vote out. A row that contributes
     * nothing (no answer, no, or yes without adults on willJoin) is never flagged - like a decline on the
     * header limit - even when the others alone overbooked the option.
     */
    public isOptionLimitExceededWithForm(option: OptionClass, vote: VoteClass): boolean {
        const max = this.getMaxNumberOfEntries(option.index);
        const choice = vote?.choiceList?.[option.index];
        if (!max || !choice) return false;
        const formContribution = this.getChoiceContribution(choice, option);
        if (formContribution === 0) return false;
        return this.getNumberOfParticipantsExcludingUser(option) + formContribution > max;
    }

    /** every vote the database holds; the viewer's form is not in the list */
    get actualResponsesCount(): number {
        return this.votesService.voteClassList.length
    }

    get pollPartipantsCountSinceLastWeek(): number {
        let returnValue = 0
        let oneWeekAgo = new Date();
        oneWeekAgo.setDate(new Date().getDate() - 7);
        this.votesService.voteClassList.forEach(vote => {
            const createdAt = new Date(vote.createdAt)
            if (createdAt > oneWeekAgo) {
                returnValue++
            }
        })
        return returnValue
    }

    get daysSincePollCreation() {
        const createdAt = new Date(this.headerService.headerClass.createdAt).getTime();
        const today = new Date().getTime();
        const millisecondsPerDay = 24 * 60 * 60 * 1000;
        const daysSinceCreation = Math.floor((today - createdAt) / millisecondsPerDay);
        return daysSinceCreation;
    }

    getBestOptions(type: 'date' | 'text' | 'all', numberOfBestOptions?: number): BestOptionsData[] {
        let returnValue: BestOptionsData[] = [];
        let options: OptionClass[];
        switch (type) {
            case "date":
                options = this.optionsService.dateOptions;
                break
            case "text":
                options = this.optionsService.textOptions;
                break
            case "all":
                options = this.optionsService.optionList.allOptions
        }
        if (!numberOfBestOptions) numberOfBestOptions = options.length
        options.forEach(option => {
            returnValue.push({
                option: option,
                yes: this.getQuantityOfYesVoters(option.index),
                no: this.getQuantityOfNoVoters(option.index),
                maybe: this.getQuantityOfMayeVoters(option.index)

            })
        })
        returnValue.sort((a, b) => {
            const sumA = a.yes + a.maybe
            const sumB = b.yes + b.maybe
            if (sumA === sumB && a.yes === b.yes) return 0;
            if (sumA === sumB && a.yes > b.yes) return -1;
            if (sumA === sumB && a.yes < b.yes) return 1;
            if (sumA > sumB) return -1;
            if (sumA < sumB) return 1;
        }
        )
        returnValue = returnValue.slice(0, numberOfBestOptions)
        return returnValue
    }

    getIsBestDateOrTextOption(option: OptionClass): boolean {
        return (this.getIsBestOption(option, 'date')
            || this.getIsBestOption(option, 'text'))
    }

    private getIsBestOption(option: OptionClass, type: 'date' | 'text' | 'all'): boolean {
        //there might be multiple options with exactly the same votes so they should all be best options
        const bestOptionData = this.getBestOptions(type)[0]
        const yesCount = this.getQuantityOfYesVoters(option.index)
        const mayCount = this.getQuantityOfMayeVoters(option.index)
        if (bestOptionData?.yes === 0 && bestOptionData?.maybe === 0) return false
        if (bestOptionData?.yes === yesCount && bestOptionData?.maybe === mayCount) return true
        return false
    }

    getCheckBoxAnswer(option: OptionClass, secondaryAnswer: SecondaryAnswer, secondaryAnswerIndex: number): string {
        let mutltipleChoice: MutltipleChoice = option?.mutltipleChoiceList?.getByStaticIndex(secondaryAnswerIndex)
        if (secondaryAnswer?.bool) {
            return mutltipleChoice?.label
        } else {
            return ""
        }
    }

    getDropdownOrRadioAnswer(option: OptionClass, secondaryAnswer: SecondaryAnswer, secondaryAnswerIndex: number): string {
        if (secondaryAnswerIndex === 0) {
            let mutltipleChoice: MutltipleChoice = option?.mutltipleChoiceList.getByStaticIndex(secondaryAnswer.index)
            return mutltipleChoice?.label
        } else {
            return ""
        }
    }



    getMultipleChoiceUserCount(option: OptionClass, multipleChoiceOption: MutltipleChoice): number {
        return this.getMultipleChoiceUserNames(option, multipleChoiceOption).length
    }

    getMultipleChoiceUserNames(option: OptionClass, multipleChoiceOption: MutltipleChoice): string[] {
        let userNames: string[] = []
        if (!option.isMultipleChoice) return []
        const mcIndex = multipleChoiceOption.staticIndex
        if (option.optionConfig.secondaryAnswer === 'checkboxes') {
            this.votesService.voteClassList.forEach(vote => {
                const userAnswer = vote.choiceList[option.index]
                if (userAnswer?.secondaryAnswerList[mcIndex]?.bool) {
                    userNames.push(this.getUserName(vote))
                }
            });
        } else {
            this.votesService.voteClassList.forEach(vote => {
                const userAnswer = vote.choiceList[option.index]
                const secondaryAnswer = userAnswer?.secondaryAnswerList[0]
                if (secondaryAnswer) {
                    if (secondaryAnswer.index === mcIndex)
                        userNames.push(this.getUserName(vote))
                }
            });
        }
        return userNames
    }
}
